/**
 * Credential fields central-api treats as secrets. Their values never come
 * back on a read; the credential lists them in `configured_secret_fields`
 * instead, and a blank value on update keeps the stored one.
 */
const SECRET_FIELD_PATTERN = /(^|_)(KEY|TOKEN|SECRET|PASSWORD|PAT)$/i;
const SECRET_FIELD_ALLOWLIST = [
  'ACTIVECAMPAIGN_MCP_URL',
  'BOL_CLIENT_ID',
  'BOL_ADS_CLIENT_ID',
  'MONGODB_URI',
];

type McpWithSecrets = {
  is_active?: boolean;
  configured_secret_fields?: unknown;
};

/** Whether a credential field holds a secret and is rendered masked. */
export function isMcpSecretField(
  field: string,
  mcp?: McpWithSecrets | null
): boolean {
  return (
    SECRET_FIELD_PATTERN.test(field) ||
    SECRET_FIELD_ALLOWLIST.some(
      (allowed) => allowed.toUpperCase() === field.toUpperCase()
    ) ||
    isSavedMcpSecret(field, mcp)
  );
}

/**
 * True when an existing connection already stores this secret, so the field
 * may be left blank to keep it.
 */
export function isSavedMcpSecret(
  field: string,
  mcp?: McpWithSecrets | null
): boolean {
  return (
    !!mcp &&
    mcp.is_active !== undefined &&
    Array.isArray(mcp.configured_secret_fields) &&
    mcp.configured_secret_fields.includes(field)
  );
}

/**
 * The credentials without saved secrets the user left blank, so an update
 * never asks central-api to overwrite a stored secret with nothing.
 */
export function withoutBlankSavedSecrets<T>(
  credentials: T,
  mcp?: McpWithSecrets | null
): T {
  if (!credentials || typeof credentials !== 'object') {
    return credentials;
  }
  const next: Record<string, unknown> = {
    ...(credentials as Record<string, unknown>),
  };
  Object.keys(next).forEach((field) => {
    const value = next[field];
    const blank =
      value === undefined || value === null || String(value).trim() === '';
    if (blank && isSavedMcpSecret(field, mcp)) {
      delete next[field];
    }
  });
  return next as T;
}
